import { QueryClient, QueryClientProvider, QueryKey } from '@tanstack/react-query';
import { createStore, Provider, WritableAtom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { FC, ReactNode, Suspense, useState } from 'react';
import {
  clientConfigAtom,
  currenciesAtom,
  customFormattersAtom,
  fundsAtom,
  metricsConfigAtom,
  roundsAtom,
  sectorsAtom,
  teamsAtom,
  transactionTypesAtom,
  usersAtom,
} from '../../services/state/AppConfigStateJ';
import { getForesightStore } from '../../util/jotai-store';
import { Loader } from '../Loader/Loader';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyWritableAtom = WritableAtom<any, any[], unknown>;

/** An atom and the value a sample screen should read from it. */
export type SampleSeed = readonly [AnyWritableAtom, unknown];

export const seed = (atom: AnyWritableAtom, value: unknown): SampleSeed => [atom, value];

export type SampleQueryData = readonly [QueryKey, unknown];

// The tenant's configuration the live components format against; everything else starts empty.
const APP_CONFIG_ATOMS: AnyWritableAtom[] = [
  clientConfigAtom,
  currenciesAtom,
  customFormattersAtom,
  fundsAtom,
  roundsAtom,
  metricsConfigAtom,
  sectorsAtom,
  teamsAtom,
  transactionTypesAtom,
  usersAtom,
];

/** A query client whose queries never run: only data put into it comes back out. */
export function createSampleQueryClient(data: readonly SampleQueryData[] = []): QueryClient {
  const client = new QueryClient({
    defaultOptions: { queries: { enabled: false, retry: false, staleTime: Infinity, gcTime: Infinity } },
  });
  data.forEach(([key, value]) => client.setQueryData(key, value));
  return client;
}

/** A store carrying the app's configuration and the seeds, and nothing a live component could fetch into. */
export function createSampleStore(seeds: readonly SampleSeed[], queryClient: QueryClient) {
  const app = getForesightStore();
  const store = createStore();
  APP_CONFIG_ATOMS.forEach((atom) => store.set(atom, app.get(atom)));
  store.set(queryClientAtom, queryClient);
  seeds.forEach(([atom, value]) => store.set(atom, value));
  return store;
}

interface SampleScopeProps {
  seeds: readonly SampleSeed[];
  queryData?: readonly SampleQueryData[];
  children: ReactNode;
}

/**
 * Runs live components against an isolated store and a query client that never fetches, so a
 * frosted sample shows the real screen over exactly the data it was seeded with. The scope is
 * built once per mount; components that reach for the app store directly are not covered.
 */
export const SampleScope: FC<SampleScopeProps> = ({ seeds, queryData, children }) => {
  const [scope] = useState(() => {
    const queryClient = createSampleQueryClient(queryData);
    return { queryClient, store: createSampleStore(seeds, queryClient) };
  });

  return (
    <Provider store={scope.store}>
      <QueryClientProvider client={scope.queryClient}>
        <Suspense fallback={<Loader />}>{children}</Suspense>
      </QueryClientProvider>
    </Provider>
  );
};
